import { useState } from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../redux/store/store";
import { useAppSelector } from "../../redux/store/hooks";
import {
  useGetBrokersQuery,
  useGetUserQuery,
  useRequestAccountDeletionMutation,
  useCancelAccountDeletionMutation,
} from "../../redux/api";
import { IconTrash, IconRotateCcw } from "../../components/icons";
import { logout as clearAuthSession } from "../../redux/slice/authSlice";
import { logout as clearLayoutSession } from "../../redux/slice/layoutSlice";
import { currentUserId } from "../../utils/auth";

const initials = (name?: string | null) =>
  (name ?? "")
    .split(" ")
    .map((p) => p.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

const statusTone = (status?: string | null) =>
  (status ?? "").toLowerCase() === "active" ? "badge--success" : "badge--warning";

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "—";

// Broker → Profile. The signed-in broker's own account details (matched by
// email; managed by the platform owner).
const BrokerProfile = () => {
  const dispatch = useDispatch<AppDispatch>();
  const email = useAppSelector((s) => s.layout.user.email);

  const { data: brokers = [], isLoading } = useGetBrokersQuery();
  const myId = currentUserId();
  const { data: me } = useGetUserQuery(myId ?? "", { skip: !myId });

  const [requestAccountDeletion, { isLoading: requestingDeletion }] =
    useRequestAccountDeletionMutation();
  const [cancelAccountDeletion, { isLoading: restoring }] = useCancelAccountDeletionMutation();
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!window.confirm("Delete your account? You can restore it during the grace period by signing back in.")) {
      return;
    }
    setDeleteError(null);
    try {
      await requestAccountDeletion().unwrap();
      dispatch(clearAuthSession());
      dispatch(clearLayoutSession());
    } catch (err) {
      setDeleteError((err as { message?: string })?.message ?? "Failed to delete account.");
    }
  };

  const handleRestore = async () => {
    setDeleteError(null);
    try {
      await cancelAccountDeletion().unwrap();
    } catch (err) {
      setDeleteError((err as { message?: string })?.message ?? "Failed to restore account.");
    }
  };

  const broker = brokers.find(
    (b) => (b.email ?? "").toLowerCase() === email.toLowerCase(),
  );

  const address = broker
    ? [broker.addressLine1, broker.city, broker.state, broker.country, broker.postalCode]
        .filter(Boolean)
        .join(", ")
    : "";

  return (
    <div className="dashboard">
      <section className="panel">
        {isLoading ? (
          <>
            <div className="panel__head">
              <div>
                <h2>Profile</h2>
              </div>
            </div>
            <div className="profile-empty">Loading…</div>
          </>
        ) : !broker ? (
          <>
            <div className="panel__head">
              <div>
                <h2>Broker Profile</h2>
              </div>
            </div>
            <div className="profile-empty">
              No broker account is linked to {email}.
            </div>
          </>
        ) : (
          <>
            <div className="profile-header">
              <span className="profile-avatar">{initials(broker.name)}</span>
              <div className="profile-id">
                <h2>{broker.name}</h2>
                <span className="profile-email">{broker.email ?? "—"}</span>
                <div className="profile-badges">
                  {broker.companyName ? (
                    <span className="pill pill--neutral">
                      {broker.companyName}
                    </span>
                  ) : null}
                  <span className={`badge ${statusTone(broker.status)}`}>
                    <span className="badge__dot" />
                    {broker.status}
                  </span>
                </div>
              </div>
            </div>

            <dl className="profile-grid">
              <div>
                <dt>Code</dt>
                <dd>{broker.code ?? "—"}</dd>
              </div>
              <div>
                <dt>Commission rate</dt>
                <dd>{broker.commissionRate ?? 0}%</dd>
              </div>
              <div>
                <dt>Referral code</dt>
                <dd>{broker.referralCode ?? "—"}</dd>
              </div>
              <div>
                <dt>Phone</dt>
                <dd>{broker.phone ?? "—"}</dd>
              </div>
              <div>
                <dt>Payout account</dt>
                <dd>{broker.payoutAccount ?? "—"}</dd>
              </div>
              <div>
                <dt>Address</dt>
                <dd>{address || "—"}</dd>
              </div>
            </dl>
          </>
        )}
      </section>

      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Danger zone</h2>
          </div>
        </div>

        {deleteError ? <div className="form-error">{deleteError}</div> : null}

        {me?.pendingDeletion ? (
          <>
            <p className="profile-empty">
              Your account is scheduled for deletion on{" "}
              <strong>{formatDate(me.pendingDeletion.scheduledFor)}</strong>. You can restore it
              any time before then.
            </p>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleRestore}
              disabled={restoring}
            >
              <IconRotateCcw /> {restoring ? "Restoring…" : "Restore my account"}
            </button>
          </>
        ) : (
          <>
            <p className="profile-empty">
              Deleting your account soft-deletes it for a grace period (configurable by the
              platform administrator) — you can restore it any time before then by signing back
              in.
            </p>
            <button
              type="button"
              className="btn btn--danger"
              onClick={handleDelete}
              disabled={requestingDeletion}
            >
              <IconTrash /> {requestingDeletion ? "Deleting…" : "Delete my account"}
            </button>
          </>
        )}
      </section>
    </div>
  );
};

export default BrokerProfile;
